import React, { useState, useRef, useEffect } from 'react';
import { 
  BuildingOfficeIcon, 
  MapPinIcon, 
  Cog6ToothIcon,
  CheckCircleIcon,
  BuildingStorefrontIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import revvlyLogo from '@/img/revvly.png';
import { HorarioSelectorCompact } from '../common/HorarioSelectorCompact';
import AddressAutocomplete from '../common/AddressAutocomplete';
import RutInput from '../common/RutInput';

/**
 * Componente de formulario por pasos para crear empresa
 */
export const EmpresaFormSteps = ({ 
  formData, 
  onFormDataChange, 
  onValidationChange, 
  onSubmit
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const formRef = useRef();

  const steps = [
    {
      id: 1,
      title: 'Información Básica',
      icon: BuildingOfficeIcon,
      fields: ['nombre', 'razon_social', 'rut', 'giro']
    },
    {
      id: 2,
      title: 'Contacto y Ubicación',
      icon: MapPinIcon,
      fields: ['email', 'telefono', 'direccion', 'ciudad']
    },
    {
      id: 3,
      title: 'Sucursal Principal',
      icon: BuildingStorefrontIcon,
      fields: ['sucursal_nombre', 'sucursal_direccion', 'sucursal_telefono', 'sucursal_capacidad']
    }
  ];

  // Obtener datos actuales del formulario
  const getCurrentFormData = () => {
    if (!formRef.current) return formData;
    
    const currentData = { ...formData };
    // Buscar todos los inputs, textareas y checkboxes dentro del div
    const inputs = formRef.current.querySelectorAll('input, textarea, select');
    
    inputs.forEach(element => {
      if (element.name) {
        if (element.type === 'checkbox') {
          currentData[element.name] = element.checked;
        } else {
          currentData[element.name] = element.value;
        }
      }
    });
    
    // Actualizar el estado del componente padre con los datos actuales
    onFormDataChange(currentData);
    
    return currentData;
  };

  // Validar step actual
  const validateCurrentStep = () => {
    const currentData = getCurrentFormData();
    
    // Campos requeridos por step
    const requiredFields = {
      1: ['nombre', 'rut'],
      2: ['email', 'direccion', 'ciudad'],
      3: ['sucursal_nombre', 'sucursal_direccion', 'sucursal_telefono']
    };

    const fieldLabels = {
      nombre: 'Nombre de la empresa',
      rut: 'RUT',
      email: 'Email',
      direccion: 'Dirección',
      ciudad: 'Ciudad',
      sucursal_nombre: 'Nombre de la sucursal',
      sucursal_direccion: 'Dirección de la sucursal',
      sucursal_telefono: 'Teléfono de la sucursal',
      sucursal_capacidad: 'Capacidad de clientes'
    };

    const fieldsToValidate = requiredFields[currentStep] || [];
    let isValid = true;
    const newErrors = {};

    // Validar campos requeridos
    fieldsToValidate.forEach(field => {
      const value = currentData[field] || '';
      if (!value || value.toString().trim() === '') {
        newErrors[field] = `${fieldLabels[field]} es requerido`;
        isValid = false;
      }
    });

    // Validación específica de RUT en step 1
    if (currentStep === 1 && currentData.rut) {
      const cleanRut = (rut) => rut.replace(/[^0-9kK]/g, '').toUpperCase();
      
      const validateRut = (rut) => {
        const cleaned = cleanRut(rut);
        if (cleaned.length < 8 || cleaned.length > 9) return false;
        
        const body = cleaned.slice(0, -1);
        const providedDV = cleaned.slice(-1);
        
        let sum = 0;
        let multiplier = 2;
        
        for (let i = body.length - 1; i >= 0; i--) {
          sum += parseInt(body[i]) * multiplier;
          multiplier = multiplier === 7 ? 2 : multiplier + 1;
        }
        
        const remainder = sum % 11;
        const dv = 11 - remainder;
        const calculatedDV = dv === 11 ? '0' : dv === 10 ? 'K' : dv.toString();
        
        return calculatedDV === providedDV;
      };
      
      if (!validateRut(currentData.rut)) {
        newErrors.rut = 'RUT inválido. Verifique el número y dígito verificador.';
        isValid = false;
      }
    }

    // Validación específica de email en step 2
    if (currentStep === 2 && currentData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(currentData.email)) {
        newErrors.email = 'Email no válido';
        isValid = false;
      }
    }

    setErrors(newErrors);
    onValidationChange(isValid);
    
    return isValid;
  };

  // Navegar entre steps
  const goToStep = (stepId) => {
    if (stepId < currentStep || validateCurrentStep()) {
      setCurrentStep(stepId);
    }
  };

  const nextStep = () => {
    if (validateCurrentStep() && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      setErrors({}); // Solo limpiar errores cuando se avanza exitosamente
    }
    // Si la validación falla, los errores quedan visibles
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({}); // Limpiar errores al retroceder
    }
  };


  // Componente de campo de input simple sin validación en tiempo real
  const InputField = ({ 
    name, 
    label, 
    type = 'text', 
    placeholder, 
    required = false, 
    rows
  }) => {
    const Component = rows ? 'textarea' : 'input';
    const hasError = errors[name];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
        <Component
          name={name}
          type={type}
          rows={rows}
          defaultValue={formData[name] || ''}
          className={`w-full bg-gray-800 border-2 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
            hasError 
              ? 'border-red-500 focus:border-red-500' 
              : 'border-gray-700 focus:border-blue-500'
          }`}
          placeholder={placeholder}
        />
        {hasError && (
          <p className="text-red-400 text-sm mt-1 font-medium">
            {hasError}
          </p>
        )}
      </div>
    );
  };

  // Renderizar step actual
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                name="nombre"
                label="Nombre de la Empresa"
                placeholder="Ej: Taller Automotriz Central"
                required
              />
              <InputField
                name="razon_social"
                label="Razón Social"
                placeholder="Ej: Taller Automotriz Central SpA"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  RUT
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <RutInput
                  name="rut"
                  value={formData.rut || ''}
                  onChange={(e) => {
                    const currentFormData = getCurrentFormData();
                    const updatedData = { ...currentFormData, rut: e.target.value };
                    onFormDataChange(updatedData);
                  }}
                  placeholder="Ej: 12.345.678-9"
                  required
                  className={errors.rut ? 'input-error' : ''}
                />
                {errors.rut && (
                  <p className="text-red-400 text-sm mt-1 font-medium">
                    {errors.rut}
                  </p>
                )}
              </div>
              <InputField
                name="giro"
                label="Giro Comercial"
                placeholder="Ej: Servicios de reparación automotriz"
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                name="email"
                label="Email de Contacto"
                type="email"
                placeholder="contacto@empresa.com"
                required
              />
              <InputField
                name="telefono"
                label="Teléfono"
                type="tel"
                placeholder="+56 9 1234 5678"
              />
            </div>
            
            {/* Autocompletado de dirección */}
            <AddressAutocomplete
              name="direccion"
              label="Dirección"
              placeholder="Ej: Av. Principal 123, Centro"
              required
              origin="onboarding"
              labelClassName="block text-sm font-medium text-gray-300 mb-1"
              inputClassName="w-full bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 pr-12 text-white 
     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 mb-4"
              initialValue={formData.direccion || ''}
              onAddressSelect={(locationData) => {
                // Obtener TODOS los datos actuales del formulario
                const currentFormData = getCurrentFormData();
                // Actualizar con la nueva ubicación
                const updatedData = {
                  ...currentFormData,
                  direccion: locationData.address,
                  ciudad: locationData.city || currentFormData.ciudad,
                  coordenadas: locationData.coordinates // Guardar coordenadas para uso futuro
                };
                onFormDataChange(updatedData);
              }}
            />
            
            <InputField
              name="ciudad"
              label="Ciudad"
              placeholder="Ej: Santiago, Valparaíso, Concepción"
              required
            />
          </div>
        );

      case 3:
        return (
          <div>
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-blue-400 text-sm">
                💡 Configura tu sucursal principal. Podrás agregar más sucursales desde el panel de administración.
              </p>
            </div>
            <InputField
              name="sucursal_nombre"
              label="Nombre de la Sucursal"
              placeholder="Ej: Sucursal Centro, Casa Matriz"
              required
            />
            <AddressAutocomplete
              name="sucursal_direccion"
              label="Dirección de la Sucursal"
              placeholder="Ej: Av. Principal 123, Centro"
              required
              labelClassName="block text-sm font-medium text-gray-300 mb-1"
              origin="onboarding"
              inputClassName="w-full bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 pr-12 text-white 
     focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 mb-4"
              initialValue={formData.sucursal_direccion || ''}
              onAddressSelect={(locationData) => {
                // Obtener TODOS los datos actuales del formulario
                const currentFormData = getCurrentFormData();
                // Actualizar con la nueva ubicación
                const updatedData = {
                  ...currentFormData,
                  sucursal_direccion: locationData.address,
                  sucursal_coordenadas: locationData.coordinates // Guardar coordenadas para uso futuro
                };
                onFormDataChange(updatedData);
              }}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                name="sucursal_telefono"
                label="Teléfono de la Sucursal"
                type="tel"
                placeholder="+56 9 1234 5678"
                required
              />
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <UsersIcon className="inline w-4 h-4 mr-1" />
                  Capacidad de Clientes Simultáneos
                </label>
                <input
                  name="sucursal_capacidad"
                  type="number"
                  min="1"
                  max="20"
                  defaultValue={formData.sucursal_capacidad || 1}
                  className="w-full bg-gray-800 border-2 border-gray-700 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  placeholder="1"
                  title="Cantidad máxima de clientes que pueden ser atendidos al mismo tiempo"
                />
                <p className="text-gray-400 text-xs mt-1">Número de clientes que pueden tener cita a la misma hora</p>
              </div>
            </div>
            
            {/* Selector de horarios */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Horario de Atención
              </label>
              <HorarioSelectorCompact
                defaultValue={formData.sucursal_horario}
                onChange={(horarios) => {
                  // Obtener TODOS los datos actuales del formulario
                  const currentFormData = getCurrentFormData();
                  //console.log('🕒 HorarioSelector onChange - Datos actuales:', currentFormData);
                  // Actualizar manteniendo todos los datos
                  const updatedData = { ...currentFormData, sucursal_horario: horarios };
                  //console.log('🕒 HorarioSelector onChange - Datos actualizados:', updatedData);
                  onFormDataChange(updatedData);
                }}
              />
            </div>
          </div>
        );


      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress bar */}
      <div className="mb-8">
        {/* Vista desktop */}
        <div className="hidden md:flex items-center justify-between mb-4">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isClickable = step.id <= currentStep || isCompleted;

            return (
              <div key={step.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => isClickable && goToStep(step.id)}
                  disabled={!isClickable}
                  className={`
                    relative flex items-center justify-center w-16 h-16 rounded-full flex-shrink-0
                    transition-all duration-200 ${
                      isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
                    }
                    ${isActive
                      ? 'bg-blue-600 text-white ring-4 ring-blue-600 ring-opacity-30'
                      : isCompleted
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-700 text-gray-400'
                    }
                  `}
                  style={{ minWidth: '64px', minHeight: '64px', maxWidth: '64px', maxHeight: '64px' }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon className="h-8 w-8" />
                  ) : (
                    <StepIcon className="h-8 w-8" />
                  )}
                </button>
                
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    isActive ? 'text-white' : isCompleted ? 'text-green-400' : 'text-gray-400'
                  }`}>
                    Paso {step.id}
                  </p>
                  <p className={`text-sm ${
                    isActive ? 'text-blue-400' : isCompleted ? 'text-green-300' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </p>
                </div>

                {index < steps.length - 1 && (
                  <div className={`
                    w-16 h-1 mx-4 ${
                      step.id < currentStep ? 'bg-green-600' : 'bg-gray-700'
                    }
                  `} />
                )}
              </div>
            );
          })}
        </div>

        {/* Vista móvil - Solo círculos con líneas */}
        <div className="md:hidden flex items-center justify-center mb-6">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isClickable = step.id <= currentStep || isCompleted;

            return (
              <div key={step.id} className="flex items-center">
                <button
                  type="button"
                  onClick={() => isClickable && goToStep(step.id)}
                  disabled={!isClickable}
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200
                    ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-not-allowed'}
                    ${isActive
                      ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-opacity-30'
                      : isCompleted
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-700 text-gray-400'
                    }
                  `}
                >
                  {isCompleted ? (
                    <CheckCircleIcon className="h-4 w-4" />
                  ) : (
                    <StepIcon className="h-4 w-4" />
                  )}
                </button>

                {index < steps.length - 1 && (
                  <div className={`
                    w-8 h-0.5 mx-2 ${
                      step.id < currentStep ? 'bg-green-600' : 'bg-gray-700'
                    }
                  `} />
                )}
              </div>
            );
          })}
        </div>

        {/* Título del paso actual en móvil */}
        <div className="md:hidden text-center mb-4">
          <p className="text-white font-medium">
            Paso {currentStep}: {steps.find(s => s.id === currentStep)?.title}
          </p>
        </div>
      </div>

      {/* Información del plan gratuito */}
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4 mb-8">
        <div className="flex items-center">
          <div className="w-28 h-12 mr-4 rounded-lg bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
            <img 
              src={revvlyLogo} 
              alt="Revvly" 
              className="w-20 h-8 object-contain"
            />
          </div>
          <div>
            <h3 className="text-white font-semibold">Plan Gratuito</h3>
            <p className="text-green-400 text-sm">
              Gratis - Comienza a gestionar tu taller sin costo
            </p>
          </div>
        </div>
      </div>

      {/* Contenido del step */}
      <div ref={formRef} className="bg-gray-800 rounded-xl p-8 mb-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2">
            {steps.find(s => s.id === currentStep)?.title}
          </h2>
          <p className="text-gray-400">
            Paso {currentStep} de {steps.length}
          </p>
        </div>

        {renderStepContent()}
      </div>

      {/* Navegación */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={prevStep}
          disabled={currentStep === 1}
          className="px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Anterior
        </button>

        <div className="flex space-x-3">
          {currentStep < steps.length ? (
            <button
              type="button"
              onClick={nextStep}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Siguiente
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (validateCurrentStep() && onSubmit) {
                  onSubmit();
                }
              }}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center"
            >
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Crear Empresa
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmpresaFormSteps;